import { insertInlineNode } from '@common/prosemirror/commands/insert-inline-node.command';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { findNode } from '@common/prosemirror/model/node-find';
import { EquationInsertMetaKey } from '@common/prosemirror/plugins/equation-insert.plugin';
import { getTrackedChangesState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copyNodeByApplyingChanges } from '@common/prosemirror/tracked-changes/copy';
import { NodeType, ProseMirrorNode } from 'prosemirror-model';
import { Command, EditorState, NodeSelection, TextSelection, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

/**
 * Tells the madcapequation node view at `pos` to enter edit mode. This is the only way to open
 * an existing equation for editing from outside its own node view (double-click/dblclick is
 * handled internally) — dispatching a DOM event on its element, rather than a ProseMirror
 * transaction, since entering edit mode doesn't change the document.
 */
export function dispatchEditEquationEvent(view: EditorView, pos: number) {
  const dom = view.nodeDOM(pos) as HTMLElement;
  dom?.dispatchEvent(new CustomEvent('edit-equation', { bubbles: false }));
}

/**
 * Returns a command that either opens the currently-selected madcapequation node for editing,
 * or inserts a new empty one at the current selection when none is selected. The inserting
 * client's own node view will automatically enter edit mode since the new node's content is
 * empty (see equationInsertPlugin — other collaborators' clients don't auto-enter for the same
 * node). Returns false when neither editing nor inserting is possible (disables the toolbar
 * button). The insert itself is delegated to the generic insertInlineNode — see that branch's
 * comment for how tracked changes, EquationInsertMetaKey, and the post-insert selection all still
 * get handled.
 */
export function insertOrEditEquation(equationType: NodeType): Command {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView): boolean {
    const { selection } = state;

    if (selection instanceof NodeSelection && selection.node.type === equationType) {
      if (dispatch && view) {
        dispatchEditEquationEvent(view, selection.from);
      }

      return true;
    }

    // Not editing an existing equation, so fall through to inserting a new one.
    const insertPos = selection.from;

    // insertInlineNode already knows how to wrap a new node as a tracked add (and a replaced
    // selection as a tracked remove) when tracked changes are enabled, and to leave it as a plain
    // replaceSelectionWith when they aren't — no need to duplicate that here. It dispatches its
    // own transaction rather than returning it, so EquationInsertMetaKey is tagged by wrapping the
    // dispatch callback instead of the transaction directly.
    return insertInlineNode(equationType)(state, dispatch && ((tr) => {
      // insertInlineNode drops the equation right at insertPos for a plain cursor or inline
      // selection, but when a block node is selected it can't place an inline node directly at the
      // block level and wraps it in an auto-created block (typically a paragraph), landing the
      // equation deeper than insertPos. Read the equation's real position back out of the resulting
      // document rather than assuming insertPos. It's the first equation at or after insertPos:
      // insertInlineNode always inserts the new node at (the mapped) selection start, ahead of any
      // tracked-remove copy of a replaced selection.
      const equationPos = findNode(tr.doc, (node, pos) => node.type === equationType && pos >= insertPos)?.pos ?? insertPos;

      // Tag the equation's resulting-document position so equationInsertPlugin recognizes this
      // client as the inserter and the node view auto-enters edit mode (see that plugin).
      tr.setMeta(EquationInsertMetaKey, equationPos);

      // Select the newly inserted equation node
      tr.setSelection(NodeSelection.create(tr.doc, equationPos));
      dispatch(tr.scrollIntoView());
    }), view);
  };
}

/**
 * Replaces `oldNode` at `pos` with `newNode` on `tr`, building the tracked-change wrapper directly
 * rather than relying on ChangeTracker's post-hoc diffing (atom nodes with content confuse it —
 * Bug #191092). Applies the Replace change via copyNodeByApplyingChanges so the shared
 * merge-vs-replace logic, not the caller, decides how it interacts with any pending change newNode
 * already carries forward. Shared by saveEquation() (same-type content edit) and
 * convertMathMlEquationToLatex() (promotes a bare math/mmlmath node to a madcapequation wrapper).
 */
function replaceEquationWithTrackedChange(state: EditorState, tr: Transaction, pos: number, oldNode: ProseMirrorNode, newNode: ProseMirrorNode): Transaction {
  const schema = state.schema as CollabSchema;
  const trackedChangesState = getTrackedChangesState(state);

  if (trackedChangesState?.enabled) {
    const changeAttrs = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Replace, {
      changeContent: schema.nodeToCode(oldNode)
    });
    tr.replaceWith(pos, pos + oldNode.nodeSize, copyNodeByApplyingChanges(schema.changeMark, newNode, changeAttrs));

    // Tag the transaction whenever tracked changes are enabled so ChangeTracker's
    // appendTransaction doesn't also run its own generic diff-based tracking on top of this replace.
    tr.setMeta(TrackedChangesTrackedMetaKey, true);
  } else {
    tr.replaceWith(pos, pos + oldNode.nodeSize, newNode);
  }

  return tr;
}

/**
 * Returns a command that replaces the madcapequation node at `pos` with `newNode`. Reads the old
 * node fresh from `state.doc` (returning false if `pos` no longer holds one, rather than trust a
 * caller's possibly-stale reference) and requires it to be the same node type as `newNode` — see
 * convertMathMlEquationToLatex() for the equation command that intentionally changes type.
 */
export function saveEquation(pos: number, newNode: ProseMirrorNode): Command {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    const oldNode = state.doc.nodeAt(pos);

    if (!oldNode || oldNode.type !== newNode.type) {
      return false;
    }

    if (dispatch) {
      const tr = replaceEquationWithTrackedChange(state, state.tr, pos, oldNode, newNode);
      tr.setSelection(TextSelection.create(tr.doc, pos + tr.doc.resolve(pos).nodeAfter.nodeSize));
      dispatch(tr);
    }

    return true;
  };
}

/**
 * Returns a command that converts the equation at `pos` — a madcapequation wrapper or a bare
 * math/mmlmath node with no wrapper (see madcap-equation-schema-plugin.ts's doc comment on the math
 * node) — into a LaTeX madcapequation wrapping `latex` (already converted from the equation's
 * MathML content by the caller — see convertMathMlToLatex() in ui/equation/util/equation-node.ts).
 * Unlike saveEquation(), the node at `pos` is allowed to change type: a bare math/mmlmath node is
 * promoted to a madcapequation wrapper, which MathLive's LaTeX-based editing requires (see
 * isEditableEquationNode()'s doc comment) — so a type-equality check isn't the right "did pos
 * drift" guard here; checking it's still one of the equation node types is.
 */
export function convertMathMlEquationToLatex(pos: number, latex: string): Command {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    const oldNode = state.doc.nodeAt(pos);
    const equationNodeTypes = [state.schema.nodes.madcapequation, state.schema.nodes.math, state.schema.nodes.mmlmath].filter(Boolean);

    if (!oldNode || !equationNodeTypes.includes(oldNode.type)) {
      return false;
    }

    if (dispatch) {
      const newNode = state.schema.nodes.madcapequation.create({}, state.schema.text('$' + latex + '$'));
      const tr = replaceEquationWithTrackedChange(state, state.tr, pos, oldNode, newNode);
      tr.setSelection(NodeSelection.create(tr.doc, pos));
      dispatch(tr);
    }

    return true;
  };
}
